import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, Search, Phone, Mail as MailIcon, Linkedin, FileText,
  Calendar, TrendingUp, ChevronDown, ChevronRight, Building2, Users, Globe, Pencil, Save, X,
} from 'lucide-react';
import { activitiesAPI, pipelineAPI, accountsAPI, contactsAPI } from '@/lib/api';
import { listTeam } from '@/lib/auth';
import { useContactTypes } from '@/lib/useContactTypes';
import { ContactDetailSheet } from '@/components/pipeline/ContactDetailSheet';
import { toast } from 'sonner';
import { safeFormat } from '@/lib/utils';
import { useForm } from 'react-hook-form';
import { useT } from '@/lib/LanguageContext';
import { useProject } from '@/lib/ProjectContext';

// ── Static config ────────────────────────────────────────────────────────────

const DEFAULT_STAGES = [
  { id: 'new_lead',       name: 'New Lead',        color: '#9AA3AB' },
  { id: 'contacted',      name: 'Contacted',        color: '#4C6B82' },
  { id: 'engaged',        name: 'Engaged',          color: '#A62B1F' },
  { id: 'demo_scheduled', name: 'Demo Scheduled',   color: '#F59E0B' },
  { id: 'proposal_sent',  name: 'Proposal Sent',    color: '#EC4899' },
  { id: 'won',            name: 'Won',              color: '#10B981' },
  { id: 'lost',           name: 'Lost',             color: '#5C6670' },
];

// CONTACT_TYPES is loaded dynamically via useContactTypes() inside the component

// ── DragCard (no hooks — safe inside DragOverlay) ────────────────────────────

const DragCard = ({ item, stages, isAccount }) => {
  const stage = stages.find(s => s.id === (item.stage));
  // assigned_to may be a UUID or email — just take first 2 non-hyphen chars
  const initials = item.assigned_to
    ? item.assigned_to.replace(/-/g, '').slice(0, 2).toUpperCase()
    : '??';
  return (
    <div
      className="bg-white border border-[#E3DDD1] rounded-lg p-3 shadow-xl cursor-grabbing"
      style={{ borderLeft: `4px solid ${stage?.color || '#9AA3AB'}`, minWidth: '240px' }}
    >
      <div className="flex items-start justify-between mb-1">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#1E2A33] truncate text-sm" style={{ fontFamily: 'Manrope' }}>
            {isAccount ? item.name : item.full_name}
          </p>
          {isAccount ? (
            <p className="text-xs text-[#5C6670]">{item._contacts_count || 0} contacts</p>
          ) : (
            <p className="text-xs text-[#5C6670] truncate">{item.company || 'No company'}</p>
          )}
        </div>
        <Avatar className="h-7 w-7 ml-2 flex-shrink-0" style={{ backgroundColor: '#A62B1F' }}>
          <AvatarFallback className="text-white text-xs font-medium">{initials}</AvatarFallback>
        </Avatar>
      </div>
    </div>
  );
};

// ── AccountCard (sortable) ────────────────────────────────────────────────────

const AccountCard = ({ account, contacts, stages, teamMap = {}, onClick }) => {
  const { colorFor, labelFor } = useContactTypes();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: account._id,
  });
  const stage = stages.find(s => s.id === account.stage);
  const daysInStage = account.updated_at
    ? Math.floor((Date.now() - new Date(account.updated_at)) / 86400000)
    : 0;
  const rep = account.assigned_to ? (teamMap[account.assigned_to] || null) : null;
  const repLabel = rep ? (rep.full_name || rep.email) : (account.assigned_to || '');
  const initials = repLabel ? repLabel.replace(/-/g, '').slice(0, 2).toUpperCase() : '??';

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        borderLeft: `4px solid ${stage?.color || '#9AA3AB'}`,
      }}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="bg-white border border-[#E3DDD1] rounded-lg p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Building2 className="w-3.5 h-3.5 text-[#A62B1F] flex-shrink-0" />
            <p className="font-semibold text-[#1E2A33] truncate text-sm" style={{ fontFamily: 'Manrope' }}>
              {account.name}
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#5C6670]">
            <Users className="w-3 h-3" />
            <span>{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <Avatar className="h-7 w-7 ml-2 flex-shrink-0" style={{ backgroundColor: '#A62B1F' }}>
          <AvatarFallback className="text-white text-xs font-medium">{initials}</AvatarFallback>
        </Avatar>
      </div>
      <div className="flex items-center justify-between mt-1">
        {contacts.length > 0 && (
          <div className="flex -space-x-1">
            {contacts.slice(0, 3).map((c, i) => (
              <Avatar key={c._id} className="h-5 w-5 border border-white" style={{ zIndex: 3 - i }}>
                <AvatarFallback className="text-[8px] bg-[#E3DDD1] text-[#5C6670]">
                  {(c.full_name || '?').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            ))}
            {contacts.length > 3 && (
              <span className="text-[10px] text-[#5C6670] ml-1.5 self-center">+{contacts.length - 3}</span>
            )}
          </div>
        )}
        <span className="text-xs text-[#5C6670] ml-auto">{daysInStage}d</span>
      </div>
    </div>
  );
};

// ── StandaloneContactCard (sortable) — contacts with no account ───────────────

const StandaloneContactCard = ({ contact, stages, teamMap = {}, onClick }) => {
  const t = useT();
  const { colorFor, labelFor } = useContactTypes();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: contact._id,
  });
  const stage = stages.find(s => s.id === contact.stage);
  const daysInStage = contact.updated_at
    ? Math.floor((Date.now() - new Date(contact.updated_at)) / 86400000)
    : 0;
  const rep = contact.assigned_to ? (teamMap[contact.assigned_to] || null) : null;
  const repLabel = rep ? (rep.full_name || rep.email) : (contact.assigned_to || '');
  const initials = repLabel ? repLabel.replace(/-/g, '').slice(0, 2).toUpperCase() : '??';

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        borderLeft: `4px solid ${stage?.color || '#9AA3AB'}`,
      }}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="bg-white border border-[#E3DDD1] rounded-lg p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#1E2A33] truncate text-sm" style={{ fontFamily: 'Manrope' }}>
            {contact.full_name}
          </p>
          <p className="text-xs text-[#5C6670] truncate">{contact.company || t('common.no_company')}</p>
        </div>
        <Avatar className="h-7 w-7 ml-2 flex-shrink-0" style={{ backgroundColor: '#A62B1F' }}>
          <AvatarFallback className="text-white text-xs font-medium">{initials}</AvatarFallback>
        </Avatar>
      </div>
      <div className="flex items-center justify-between">
        <Badge
          variant="outline"
          className={`text-xs ${colorFor(contact.contact_type)}`}
        >
          {labelFor(contact.contact_type) || contact.contact_type || 'Other'}
        </Badge>
        <span className="text-xs text-[#5C6670]">{daysInStage}d</span>
      </div>
    </div>
  );
};

// ── StageColumn ───────────────────────────────────────────────────────────────

const StageColumn = ({
  stage, stages,
  accounts, standaloneContacts,
  onAccountClick, onContactClick,
  onAdd, height = '420px', teamMap = {},
}) => {
  const t = useT();
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const totalCount = accounts.length + standaloneContacts.length;
  const allIds = [
    ...accounts.map(({ account }) => account._id),
    ...standaloneContacts.map(c => c._id),
  ];

  return (
    <div
      ref={setNodeRef}
      className="border border-[#E3DDD1] rounded-xl p-3 flex flex-col flex-shrink-0"
      style={{
        width: '240px',
        height,
        backgroundColor: isOver ? '#F7ECEA' : 'white',
        transition: 'background-color 0.15s ease',
      }}
    >
      {/* Header */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1.5">
          <h3
            className="font-semibold text-sm"
            style={{
              fontFamily: 'Manrope',
              color: stage.id === 'won' ? '#10B981' : stage.id === 'lost' ? '#5C6670' : '#1E2A33',
            }}
          >
            {stage.name}
          </h3>
          <span className="text-xs bg-[#F3EFE7] text-[#5C6670] rounded-full px-2 py-0.5 font-medium">
            {totalCount}
          </span>
        </div>
        <div className="h-0.5 rounded-full" style={{ backgroundColor: stage.color, opacity: 0.5 }} />
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto pr-0.5">
        <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
          {accounts.map(({ account, contacts }) => (
            <AccountCard
              key={account._id}
              account={account}
              contacts={contacts || []}
              stages={stages}
              teamMap={teamMap}
              onClick={() => onAccountClick(account)}
            />
          ))}
          {standaloneContacts.map(contact => (
            <StandaloneContactCard
              key={contact._id}
              contact={contact}
              stages={stages}
              teamMap={teamMap}
              onClick={() => onContactClick(contact)}
            />
          ))}
        </SortableContext>
        {totalCount === 0 && (
          <div className="text-center py-6 text-xs text-[#9AA3AB]">{t('pipeline.empty')}</div>
        )}
      </div>

      {/* Add button */}
      <Button
        variant="ghost"
        size="sm"
        className="mt-1.5 w-full justify-start text-xs text-[#5C6670] hover:text-[#1E2A33] hover:bg-[#F3EFE7] h-7"
        onClick={onAdd}
      >
        <Plus className="w-3 h-3 mr-1" /> {t('common.add')}
      </Button>
    </div>
  );
};

// ── AccountDetailSheet ────────────────────────────────────────────────────────

const AccountDetailSheet = ({ account, contacts, branches = [], siblingAccounts = [], stages, isOpen, onClose, onUpdate, onOpenAccount, teamMap = {} }) => {
  const t = useT();
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(null);
  const [activityTagContactId, setActivityTagContactId] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const activityForm = useForm();
  const [localStage, setLocalStage] = useState(account?.stage);
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddBranch, setShowAddBranch] = useState(false);
  const addContactForm = useForm();
  const { types: contactTypeOptions } = useContactTypes();

  useEffect(() => {
    setLocalStage(account?.stage);
    setEditMode(false);
    setShowAddContact(false);
    if (isOpen && account) loadActivities();
  }, [isOpen, account?._id]);

  const loadActivities = async () => {
    try {
      setLoadingActivities(true);
      const res = await activitiesAPI.getByAccount(account._id).catch(() => ({ data: [] }));
      setActivities(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      setLocalStage(newStage);
      await pipelineAPI.moveAccount(account._id, newStage);
      toast.success(t('pipeline.stage_updated'));
      onUpdate();
    } catch {
      toast.error('Failed to update stage');
      setLocalStage(account?.stage);
    }
  };

  const handleLogActivity = async (data) => {
    try {
      await activitiesAPI.create({
        account_id: account._id,
        contact_id: activityTagContactId || undefined,
        project_id: account.project_id || null,
        type: showActivityForm,
        subject: data.subject || showActivityForm,
        body: data.notes || '',
        direction: 'outbound',
      });
      toast.success(t('pipeline.activity'));
      activityForm.reset();
      setShowActivityForm(null);
      setActivityTagContactId('');
      loadActivities();
    } catch {
      toast.error('Failed to log activity');
    }
  };

  const handleEnterEdit = () => {
    setEditFields({
      assigned_to: account.assigned_to || '',
      deal_value: account.deal_value ?? '',
      notes: account.notes || '',
      website: account.website || '',
      main_email: account.main_email || '',
      main_phone: account.main_phone || '',
      street: account.street || '',
      city: account.city || '',
      state: account.state || '',
      postal_code: account.postal_code || '',
      country: account.country || '',
      parent_account_id: account.parent_account_id || '',
    });
    setEditMode(true);
  };

  const handleSaveAccount = async () => {
    try {
      const payload = {};
      if (editFields.assigned_to !== undefined) payload.assigned_to = editFields.assigned_to || null;
      if (editFields.deal_value !== '') payload.deal_value = parseFloat(editFields.deal_value) || null;
      if (editFields.notes !== undefined) payload.notes = editFields.notes;
      if (editFields.website !== undefined) payload.website = editFields.website;
      if (editFields.main_email !== undefined) payload.main_email = editFields.main_email;
      if (editFields.main_phone !== undefined) payload.main_phone = editFields.main_phone;
      if (editFields.street !== undefined) payload.street = editFields.street;
      if (editFields.city !== undefined) payload.city = editFields.city;
      if (editFields.state !== undefined) payload.state = editFields.state;
      if (editFields.postal_code !== undefined) payload.postal_code = editFields.postal_code;
      if (editFields.country !== undefined) payload.country = editFields.country;
      if (editFields.parent_account_id !== undefined) payload.parent_account_id = editFields.parent_account_id || '';
      await accountsAPI.update(account._id, payload);
      toast.success('Account updated');
      setEditMode(false);
      onUpdate();
    } catch {
      toast.error('Failed to update account');
    }
  };

  const handleAddContact = async (data) => {
    try {
      await contactsAPI.create({
        full_name: data.full_name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        position: data.position || undefined,
        contact_type: data.contact_type || undefined,
        account_id: account._id,
        project_id: account.project_id,
      });
      toast.success('Contact added');
      addContactForm.reset();
      setShowAddContact(false);
      onUpdate();
    } catch {
      toast.error('Failed to add contact');
    }
  };

  const iconFor = (type) => {
    const map = { call: Phone, email: MailIcon, linkedin: Linkedin, meeting: Calendar, stage_change: TrendingUp };
    return map[type] || FileText;
  };

  if (!account) return null;

  return (
    <>
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#A62B1F]" />
              <SheetTitle className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                {account.name}
              </SheetTitle>
            </div>
            {!editMode ? (
              <Button variant="ghost" size="sm" onClick={handleEnterEdit} className="h-8 gap-1.5 text-xs text-[#5C6670]">
                <Pencil className="w-3.5 h-3.5" />Edit
              </Button>
            ) : (
              <div className="flex gap-1.5">
                <Button size="sm" onClick={handleSaveAccount} className="h-8 gap-1.5 text-xs" style={{ backgroundColor: '#A62B1F', color: 'white' }}>
                  <Save className="w-3.5 h-3.5" />Save
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditMode(false)} className="h-8 px-2">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
          {!editMode && account.parent_account_id && (
            <SheetDescription>
              Branch of {siblingAccounts.find(a => a._id === account.parent_account_id)?.name || 'another company'}
            </SheetDescription>
          )}
          {!editMode && account.website && (
            <SheetDescription>
              <a href={account.website} target="_blank" rel="noopener noreferrer" className="text-[#4C6B82] hover:underline flex items-center gap-1">
                <Globe className="w-3 h-3" />{account.website}
              </a>
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="mt-6 space-y-5">

          {/* Stage */}
          <div>
            <Label className="text-sm font-medium text-[#1E2A33]">{t('pipeline.stage')}</Label>
            <Select value={localStage} onValueChange={handleStageChange}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {stages.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {editMode ? (
            <div className="space-y-3 p-3 border border-[#E3DDD1] rounded-lg bg-[#FBF8F2]">
              <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide">Edit Account Details</p>
              {siblingAccounts.length > 0 && (
                <div>
                  <Label className="text-xs text-[#5C6670]">Parent Company</Label>
                  <Select
                    value={editFields.parent_account_id || 'none'}
                    onValueChange={v => setEditFields(f => ({ ...f, parent_account_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="None — top-level company" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None — top-level company</SelectItem>
                      {siblingAccounts.filter(a => a._id !== account._id).map(a => (
                        <SelectItem key={a._id} value={a._id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#5C6670] mt-1">Set this if "{account.name}" is a branch/location of another company.</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-[#5C6670]">Website</Label>
                <Input
                  value={editFields.website}
                  onChange={e => setEditFields(f => ({ ...f, website: e.target.value }))}
                  placeholder="https://example.com"
                  className="mt-1 h-8 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-[#5C6670]">Main Email</Label>
                  <Input
                    value={editFields.main_email}
                    onChange={e => setEditFields(f => ({ ...f, main_email: e.target.value }))}
                    placeholder="info@example.com"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#5C6670]">Main Phone</Label>
                  <Input
                    value={editFields.main_phone}
                    onChange={e => setEditFields(f => ({ ...f, main_phone: e.target.value }))}
                    placeholder="(555) 555-5555"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#5C6670]">Street Address</Label>
                <Input
                  value={editFields.street}
                  onChange={e => setEditFields(f => ({ ...f, street: e.target.value }))}
                  placeholder="123 Main St"
                  className="mt-1 h-8 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-[#5C6670]">City</Label>
                  <Input
                    value={editFields.city}
                    onChange={e => setEditFields(f => ({ ...f, city: e.target.value }))}
                    placeholder="City"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#5C6670]">State / Province</Label>
                  <Input
                    value={editFields.state}
                    onChange={e => setEditFields(f => ({ ...f, state: e.target.value }))}
                    placeholder="State"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-[#5C6670]">Postal Code</Label>
                  <Input
                    value={editFields.postal_code}
                    onChange={e => setEditFields(f => ({ ...f, postal_code: e.target.value }))}
                    placeholder="Postal Code"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#5C6670]">Country</Label>
                  <Input
                    value={editFields.country}
                    onChange={e => setEditFields(f => ({ ...f, country: e.target.value }))}
                    placeholder="Country"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-[#5C6670]">{t('pipeline.assigned_to')}</Label>
                  <Select value={editFields.assigned_to || ''} onValueChange={v => setEditFields(f => ({ ...f, assigned_to: v }))}>
                    <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Unassigned</SelectItem>
                      {Object.values(teamMap).map(m => (
                        <SelectItem key={m.email} value={m.email}>{m.full_name || m.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#5C6670]">Deal Value ($)</Label>
                  <Input
                    type="number"
                    value={editFields.deal_value}
                    onChange={e => setEditFields(f => ({ ...f, deal_value: e.target.value }))}
                    placeholder="0"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#5C6670]">{t('common.notes')}</Label>
                <Textarea
                  value={editFields.notes}
                  onChange={e => setEditFields(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Notes..."
                  rows={2}
                  className="mt-1 text-sm"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-[#5C6670] mb-1">{t('pipeline.assigned_to')}</p>
                  <p className="text-[#1E2A33]">
                    {account.assigned_to
                      ? (teamMap[account.assigned_to]?.full_name || teamMap[account.assigned_to]?.email || account.assigned_to.slice(0, 8) + '…')
                      : t('pipeline.unassigned')}
                  </p>
                </div>
                {account.deal_value != null && (
                  <div>
                    <p className="text-xs text-[#5C6670] mb-1">Deal Value</p>
                    <p className="text-[#1E2A33] font-medium">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(account.deal_value)}
                    </p>
                  </div>
                )}
              </div>
              {(account.main_email || account.main_phone) && (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {account.main_email && (
                    <div>
                      <p className="text-xs text-[#5C6670] mb-1">Main Email</p>
                      <a href={`mailto:${account.main_email}`} className="text-[#4C6B82] hover:underline">{account.main_email}</a>
                    </div>
                  )}
                  {account.main_phone && (
                    <div>
                      <p className="text-xs text-[#5C6670] mb-1">Main Phone</p>
                      <a href={`tel:${account.main_phone}`} className="text-[#4C6B82] hover:underline">{account.main_phone}</a>
                    </div>
                  )}
                </div>
              )}
              {(account.street || account.city || account.state || account.postal_code || account.country) && (
                <div>
                  <p className="text-xs text-[#5C6670] mb-1">Address</p>
                  <p className="text-sm text-[#1E2A33]">
                    {[account.street, account.city, account.state, account.postal_code, account.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
              {account.notes && (
                <div>
                  <p className="text-xs text-[#5C6670] mb-1">{t('common.notes')}</p>
                  <p className="text-sm text-[#1E2A33] whitespace-pre-wrap">{account.notes}</p>
                </div>
              )}
            </>
          )}

          <Separator />

          {/* Contacts inside this account */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-[#1E2A33] text-sm" style={{ fontFamily: 'Manrope' }}>
                <Users className="w-4 h-4 inline mr-1.5 text-[#5C6670]" />
                Contacts ({contacts.length})
              </p>
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setShowAddContact(v => !v)}>
                <Plus className="w-3 h-3" />{showAddContact ? 'Cancel' : 'Add Contact'}
              </Button>
            </div>
            {showAddContact && (
              <form onSubmit={addContactForm.handleSubmit(handleAddContact)} className="mb-3 p-3 border border-[#E3DDD1] rounded-lg bg-[#FBF8F2] space-y-2">
                <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide">New Contact</p>
                <Input {...addContactForm.register('full_name', { required: true })} placeholder="Full Name *" className="h-8 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <Input {...addContactForm.register('email')} placeholder="Email" className="h-8 text-sm" />
                  <Input {...addContactForm.register('phone')} placeholder="Phone" className="h-8 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input {...addContactForm.register('position')} placeholder="Position" className="h-8 text-sm" />
                  <Select onValueChange={v => addContactForm.setValue('contact_type', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      {contactTypeOptions.map(ct => (
                        <SelectItem key={ct.id} value={ct.id}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" size="sm" className="h-7 text-xs" style={{ backgroundColor: '#A62B1F', color: 'white' }}>
                  Add Contact
                </Button>
              </form>
            )}
            {contacts.length === 0 ? (
              <p className="text-xs text-[#5C6670]">No contacts linked to this account yet.</p>
            ) : (
              <div className="space-y-2">
                {contacts.map(c => (
                  <div
                    key={c._id}
                    onClick={() => setSelectedContact(c)}
                    className="flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-[#F8EFED] hover:border-[#A62B1F] cursor-pointer transition-colors group"
                  >
                    <Avatar className="h-9 w-9 flex-shrink-0">
                      <AvatarFallback className="text-xs font-semibold bg-[#F7ECEA] text-[#A62B1F]">
                        {(c.full_name || '?').slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1E2A33] truncate">{c.full_name}</p>
                      {c.position && <p className="text-xs text-[#5C6670] truncate">{c.position}</p>}
                      {c.email && (
                        <p className="text-xs text-[#4C6B82] truncate flex items-center gap-1 mt-0.5">
                          <MailIcon className="w-3 h-3 flex-shrink-0" />
                          {c.email}
                        </p>
                      )}
                      {c.phone && (
                        <p className="text-xs text-[#5C6670] truncate flex items-center gap-1">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          {c.phone}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#CFC8BB] group-hover:text-[#A62B1F] flex-shrink-0 transition-colors" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Branches / locations of this company */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-[#1E2A33] text-sm" style={{ fontFamily: 'Manrope' }}>
                <Building2 className="w-4 h-4 inline mr-1.5 text-[#5C6670]" />
                Branches ({branches.length})
              </p>
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setShowAddBranch(true)}>
                <Plus className="w-3 h-3" />Add Branch
              </Button>
            </div>
            {branches.length === 0 ? (
              <p className="text-xs text-[#5C6670]">No branches/locations added yet.</p>
            ) : (
              <div className="space-y-2">
                {branches.map(({ account: b, contacts: bContacts }) => (
                  <div
                    key={b._id}
                    onClick={() => onOpenAccount?.(b, bContacts || [])}
                    className="flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-[#F8EFED] hover:border-[#A62B1F] cursor-pointer transition-colors group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#F8EFED] flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-[#A62B1F]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1E2A33] truncate">{b.name}</p>
                      {(b.street || b.city) && (
                        <p className="text-xs text-[#5C6670] truncate">{[b.street, b.city, b.state].filter(Boolean).join(', ')}</p>
                      )}
                      <p className="text-xs text-[#5C6670]">
                        {(bContacts || []).length} contact{(bContacts || []).length !== 1 ? 's' : ''}
                        {(b.main_email || b.main_phone) && ' · has main contact info'}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#CFC8BB] group-hover:text-[#A62B1F] flex-shrink-0 transition-colors" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Log activity */}
          <div>
            <p className="font-semibold text-[#1E2A33] text-sm mb-2" style={{ fontFamily: 'Manrope' }}>{t('pipeline.log_activity')}</p>
            <div className="grid grid-cols-2 gap-2">
              {[['call', Phone, t('pipeline.log_call')], ['email', MailIcon, t('pipeline.log_email')], ['linkedin', Linkedin, t('pipeline.log_linkedin')], ['note', FileText, t('pipeline.log_note')]].map(([type, Icon, label]) => (
                <Button key={type} variant="outline" size="sm" onClick={() => setShowActivityForm(type)} className="justify-start text-xs">
                  <Icon className="w-3 h-3 mr-1.5" />{label}
                </Button>
              ))}
            </div>
            {showActivityForm && (
              <form onSubmit={activityForm.handleSubmit(handleLogActivity)} className="mt-3 p-3 border rounded-lg space-y-2">
                {contacts.length > 0 && (
                  <Select value={activityTagContactId || 'none'} onValueChange={(v) => setActivityTagContactId(v === 'none' ? '' : v)}>
                    <SelectTrigger className="text-sm h-9">
                      <SelectValue placeholder="Who is this about?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Company-wide (no specific person)</SelectItem>
                      {contacts.map(c => (
                        <SelectItem key={c._id} value={c._id}>{c.full_name}{c.position ? ` · ${c.position}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input {...activityForm.register('subject')} placeholder={t('pipeline.subject_optional')} className="text-sm" />
                <Textarea {...activityForm.register('notes')} placeholder={t('pipeline.notes')} rows={2} className="text-sm" />
                <div className="flex gap-2">
                  <Button type="submit" size="sm" style={{ backgroundColor: '#A62B1F', color: 'white' }}>{t('pipeline.save')}</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => { setShowActivityForm(null); setActivityTagContactId(''); }}>{t('pipeline.cancel')}</Button>
                </div>
              </form>
            )}
          </div>

          <Separator />

          {/* Activity log */}
          <div>
            <p className="font-semibold text-[#1E2A33] text-sm mb-3" style={{ fontFamily: 'Manrope' }}>{t('pipeline.activity')}</p>
            {loadingActivities ? (
              <p className="text-sm text-[#5C6670]">{t('common.loading')}</p>
            ) : activities.length === 0 ? (
              <p className="text-sm text-[#5C6670]">{t('pipeline.no_activities')}</p>
            ) : (
              <div className="space-y-2">
                {activities.map(a => {
                  const Icon = iconFor(a.type);
                  return (
                    <div key={a._id} className="flex gap-2 p-2 border rounded-lg">
                      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#F3EFE7] flex-shrink-0">
                        <Icon className="w-3.5 h-3.5 text-[#5C6670]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-xs font-medium text-[#1E2A33]">{a.subject || a.type}</p>
                          {a.contact_name && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#F8ECE9] text-[#A62B1F]">
                              {a.contact_name}
                            </span>
                          )}
                        </div>
                        {a.body && (
                          <div
                            className="tiptap-preview text-xs text-[#5C6670] mt-0.5 max-h-24 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: a.body }}
                          />
                        )}
                        <p className="text-xs text-[#5C6670] mt-0.5">
                          {safeFormat(a.occurred_at, 'MMM d, yyyy')} · {a.performed_by}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>

    {/* Contact drill-down — opens on top of account sheet */}
    {selectedContact && (
      <ContactDetailSheet
        contact={selectedContact}
        stages={stages}
        isOpen={!!selectedContact}
        onClose={() => setSelectedContact(null)}
        onUpdate={onUpdate}
        teamMap={teamMap}
      />
    )}

    {/* Add Branch — opens on top of account sheet */}
    <NewAccountSheet
      isOpen={showAddBranch}
      onClose={() => setShowAddBranch(false)}
      onSuccess={onUpdate}
      parentAccount={account}
    />
    </>
  );
};


// ── NewAccountSheet ───────────────────────────────────────────────────────────

const NewAccountSheet = ({ isOpen, onClose, onSuccess, initialStage, projects, initialProject, parentAccount }) => {
  const t = useT();
  const { register, handleSubmit, reset, setValue } = useForm();
  const [selectedProjectId, setSelectedProjectId] = useState(initialProject?._id || '');

  useEffect(() => {
    if (parentAccount?.project_id) setSelectedProjectId(parentAccount.project_id);
    else if (initialProject?._id) setSelectedProjectId(initialProject._id);
  }, [initialProject?._id, parentAccount?.project_id]);

  const onSubmit = async (data) => {
    try {
      await accountsAPI.create({
        name: data.name,
        project_id: selectedProjectId,
        stage: initialStage || 'new_lead',
        parent_account_id: parentAccount?._id || undefined,
        assigned_to: data.assigned_to || undefined,
        website: data.website || undefined,
        main_email: data.main_email || undefined,
        main_phone: data.main_phone || undefined,
        street: data.street || undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        postal_code: data.postal_code || undefined,
        country: data.country || undefined,
        notes: data.notes || undefined,
      });
      toast.success(parentAccount ? 'Branch created' : 'Account created');
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(parentAccount ? 'Failed to create branch' : 'Failed to create account');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
            {parentAccount ? `New Branch of ${parentAccount.name}` : 'New Account'}
          </SheetTitle>
          <SheetDescription>{parentAccount ? 'Add a branch/location for this company' : 'Add a company to the pipeline'}</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          {!parentAccount && (
            <div>
              <Label className="text-sm">Project *</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p._id} value={p._id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: p.color || '#A62B1F' }} />
                        {p.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-sm">{parentAccount ? 'Branch/Location Name *' : 'Company Name *'}</Label>
            <Input {...register('name', { required: true })} placeholder={parentAccount ? 'Downtown Branch' : 'Acme Corp'} className="mt-1" />
          </div>
          <div>
            <Label className="text-sm">Website</Label>
            <Input {...register('website')} placeholder="https://acme.com" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-sm">Main Email</Label>
              <Input {...register('main_email')} placeholder="info@acme.com" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Main Phone</Label>
              <Input {...register('main_phone')} placeholder="(555) 555-5555" className="mt-1" />
            </div>
          </div>
          <p className="text-xs text-[#5C6670] -mt-2">Use these if you don't have a named contact yet.</p>
          <div>
            <Label className="text-sm">Street Address</Label>
            <Input {...register('street')} placeholder="123 Main St" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-sm">City</Label>
              <Input {...register('city')} placeholder="City" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">State / Province</Label>
              <Input {...register('state')} placeholder="State" className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-sm">Postal Code</Label>
              <Input {...register('postal_code')} placeholder="Postal Code" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Country</Label>
              <Input {...register('country')} placeholder="Country" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-sm">{t('common.notes')}</Label>
            <Textarea {...register('notes')} placeholder="Any notes..." rows={2} className="mt-1" />
          </div>
          <Separator />
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('pipeline.cancel')}</Button>
            <Button type="submit" style={{ backgroundColor: '#A62B1F', color: 'white' }}>Create Account</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

// ── ProjectPipelineSection ────────────────────────────────────────────────────

const ProjectPipelineSection = ({
  project,
  accounts,          // [{ account, contacts }]
  standaloneContacts,
  searchQuery,
  onAccountClick,
  onContactClick,
  onAddAccount,
  onUpdate,
  teamMap = {},
}) => {
  const t = useT();
  const [collapsed, setCollapsed] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Build flat lookup for drag tracking
  const accountMap = {};
  const contactMap = {};
  accounts.forEach(({ account, contacts }) => {
    accountMap[account._id] = { account, contacts };
    account._contacts = contacts; // attach for DragCard
  });
  standaloneContacts.forEach(c => { contactMap[c._id] = c; });

  const [localAccounts, setLocalAccounts] = useState(accounts);
  const [localStandalones, setLocalStandalones] = useState(standaloneContacts);

  useEffect(() => { setLocalAccounts(accounts); }, [accounts]);
  useEffect(() => { setLocalStandalones(standaloneContacts); }, [standaloneContacts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const stages = project.pipeline_stages?.length ? project.pipeline_stages : DEFAULT_STAGES;

  const filterAccounts = (accs) => accs.filter(({ account, contacts }) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    // Match on account/company name
    if (account.name?.toLowerCase().includes(q)) return true;
    // Match on any contact's name within the account
    if ((contacts || []).some(c => c.full_name?.toLowerCase().includes(q))) return true;
    return false;
  });

  const filterContacts = (contacts) => contacts.filter(c => {
    if (!searchQuery) return true;
    return (
      c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.company?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getAccountsForStage = (stageId) =>
    filterAccounts(localAccounts.filter(({ account }) => account.stage === stageId));

  const getContactsForStage = (stageId) =>
    filterContacts(localStandalones.filter(c => c.stage === stageId));

  const totalVisible = stages.reduce(
    (sum, s) => sum + getAccountsForStage(s.id).length + getContactsForStage(s.id).length, 0
  );

  const dragItem = activeId
    ? (accountMap[activeId] ? { ...accountMap[activeId].account, _contacts: accountMap[activeId].contacts } : null) ||
      contactMap[activeId] || null
    : null;
  const dragIsAccount = activeId ? !!accountMap[activeId] : false;

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    const draggedId = active.id;
    const stageIds = stages.map(s => s.id);

    // Determine target stage (dropped on column or on another card)
    const targetStage = stageIds.includes(over.id)
      ? over.id
      : stages.find(s => {
          const accIds = getAccountsForStage(s.id).map(({ account }) => account._id);
          const cIds = getContactsForStage(s.id).map(c => c._id);
          return [...accIds, ...cIds].includes(over.id);
        })?.id;

    if (!targetStage) return;

    // Account drag
    if (accountMap[draggedId]) {
      const { account } = accountMap[draggedId];
      if (account.stage === targetStage) return;
      // Optimistic
      setLocalAccounts(prev =>
        prev.map(({ account: a, contacts }) =>
          a._id === draggedId ? { account: { ...a, stage: targetStage }, contacts } : { account: a, contacts }
        )
      );
      try {
        await pipelineAPI.moveAccount(draggedId, targetStage);
        toast.success(`Moved to ${stages.find(s => s.id === targetStage)?.name || targetStage}`);
        onUpdate();
      } catch {
        toast.error('Failed to move account');
        setLocalAccounts(accounts);
      }
      return;
    }

    // Standalone contact drag
    if (contactMap[draggedId]) {
      const contact = contactMap[draggedId];
      if (contact.stage === targetStage) return;
      setLocalStandalones(prev =>
        prev.map(c => c._id === draggedId ? { ...c, stage: targetStage } : c)
      );
      try {
        await pipelineAPI.moveStage(draggedId, targetStage);
        toast.success(`Moved to ${stages.find(s => s.id === targetStage)?.name || targetStage}`);
        onUpdate();
      } catch {
        toast.error('Failed to move contact');
        setLocalStandalones(standaloneContacts);
      }
    }
  };

  return (
    <div className="mb-8">
      {/* Project header */}
      <div
        className="flex items-center gap-3 mb-3 group cursor-pointer select-none"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: project.color || '#A62B1F' }} />
        <h2 className="font-bold text-[#1E2A33] text-base" style={{ fontFamily: 'Manrope' }}>{project.name}</h2>
        <span className="text-xs text-white rounded-full px-2 py-0.5 font-medium" style={{ backgroundColor: project.color || '#A62B1F' }}>
          {totalVisible}
        </span>
        {project.description && (
          <span className="text-xs text-[#9AA3AB] truncate max-w-xs hidden sm:block">{project.description}</span>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-[#5C6670] hover:text-[#1E2A33] hover:bg-[#F3EFE7] opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={e => { e.stopPropagation(); onAddAccount(project, 'new_lead'); }}
          >
            <Plus className="w-3 h-3 mr-1" /> Add Account
          </Button>
          <ChevronDown
            className={`w-4 h-4 text-[#9AA3AB] transition-transform duration-200 ${collapsed ? '-rotate-90' : ''}`}
          />
        </div>
      </div>

      {!collapsed && (
        <div className="overflow-x-auto pb-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
              {stages.map(stage => (
                <StageColumn
                  key={stage.id}
                  stage={stage}
                  stages={stages}
                  accounts={getAccountsForStage(stage.id)}
                  standaloneContacts={getContactsForStage(stage.id)}
                  onAccountClick={onAccountClick}
                  onContactClick={onContactClick}
                  onAdd={() => onAddAccount(project, stage.id)}
                  height="400px"
                  teamMap={teamMap}
                />
              ))}
            </div>
            <DragOverlay dropAnimation={null}>
              {dragItem ? <DragCard item={dragItem} stages={stages} isAccount={dragIsAccount} /> : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      <div className="border-b border-[#E3DDD1] mt-1" />
    </div>
  );
};

// ── Main Pipeline component ───────────────────────────────────────────────────

export default function Pipeline() {
  const t = useT();
  const { activeProject } = useProject();
  const [projectPipelines, setProjectPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [teamMap, setTeamMap] = useState({}); // id/email → member

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountContacts, setSelectedAccountContacts] = useState([]);
  const [selectedAccountBranches, setSelectedAccountBranches] = useState([]);
  const [selectedAccountSiblings, setSelectedAccountSiblings] = useState([]);
  const [selectedAccountStages, setSelectedAccountStages] = useState(DEFAULT_STAGES);
  const [showAccountDetail, setShowAccountDetail] = useState(false);

  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContactStages, setSelectedContactStages] = useState(DEFAULT_STAGES);
  const [showContactDetail, setShowContactDetail] = useState(false);

  const [visibleProjectIds, setVisibleProjectIds] = useState(null); // null = all
  const [showProjectFilter, setShowProjectFilter] = useState(false);

  // Follow the app-wide active project (same source of truth as Scheduling,
  // Reporting and Tasks), so the filter holds across page navigation instead
  // of resetting to "all" every time you come back to Pipeline.
  useEffect(() => {
    if (activeProject?._id && visibleProjectIds === null) {
      setVisibleProjectIds([activeProject._id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?._id]);

  const [showNew, setShowNew] = useState(false);
  const [newAccountProject, setNewAccountProject] = useState(null);
  const [newAccountStage, setNewAccountStage] = useState('new_lead');

  const loadPipeline = useCallback(async () => {
    try {
      setLoading(true);
      const [pipeRes, teamRes] = await Promise.all([
        pipelineAPI.get({}),
        listTeam().catch(() => []),
      ]);
      const data = pipeRes.data || [];
      setProjectPipelines(data);

      // Build teamMap keyed by id, email for flexible lookup (team API returns id not _id)
      const map = {};
      (Array.isArray(teamRes) ? teamRes : []).forEach(m => {
        if (m.id) map[m.id] = m;
        if (m.email) map[m.email] = m;
      });
      setTeamMap(map);

      // Auto-migrate any standalone contacts that have a company name but no account yet.
      // The migration endpoint is idempotent — safe to run whenever there are unlinked contacts.
      const hasUnlinkedWithCompany = data.some(pp =>
        (pp.standalone_contacts || []).some(c => c.company?.trim())
      );
      if (hasUnlinkedWithCompany) {
        setMigrating(true);
        try {
          await accountsAPI.migrate();
          const refreshed = await pipelineAPI.get({});
          setProjectPipelines(refreshed.data || []);
        } catch (e) {
          console.error('Migration failed:', e);
        } finally {
          setMigrating(false);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);

  const handleAccountClick = (account, contacts, stages, branches, siblings) => {
    setSelectedAccount(account);
    setSelectedAccountContacts(contacts || []);
    setSelectedAccountBranches(branches || []);
    setSelectedAccountSiblings(siblings || []);
    setSelectedAccountStages(stages);
    setShowAccountDetail(true);
  };

  const handleContactClick = (contact, stages) => {
    setSelectedContact(contact);
    setSelectedContactStages(stages);
    setShowContactDetail(true);
  };

  const handleAddAccount = (project, stageId) => {
    setNewAccountProject(project);
    setNewAccountStage(stageId);
    setShowNew(true);
  };

  const allProjects = projectPipelines.map(pp => pp.project);

  // Filtered pipelines based on visible project selection
  const visiblePipelines = visibleProjectIds === null
    ? projectPipelines
    : projectPipelines.filter(pp => visibleProjectIds.includes(pp.project._id));

  const totalAccounts = visiblePipelines.reduce(
    (sum, pp) => sum + (pp.accounts?.length || 0) + (pp.standalone_contacts?.length || 0), 0
  );

  const toggleProject = (id) => {
    setVisibleProjectIds(prev => {
      const current = prev ?? allProjects.map(p => p._id);
      return current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    });
  };

  const allSelected = visibleProjectIds === null || visibleProjectIds.length === allProjects.length;

  return (
    <Layout title="Pipeline">
      {/* Top bar */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6670]" />
          <Input
            placeholder="Search companies or contacts..."
            className="pl-9 text-sm"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Project filter dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 text-sm border-[#E3DDD1] text-[#5C6670]"
            onClick={() => setShowProjectFilter(p => !p)}
          >
            <span>Projects</span>
            {!allSelected && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#A62B1F' }}>
                {visibleProjectIds?.length || 0}
              </span>
            )}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showProjectFilter ? 'rotate-180' : ''}`} />
          </Button>

          {showProjectFilter && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-40" onClick={() => setShowProjectFilter(false)} />
              <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-[#E3DDD1] rounded-xl shadow-lg p-2 min-w-[220px]">
                {/* All toggle */}
                <button
                  onClick={() => { setVisibleProjectIds(null); setShowProjectFilter(false); }}
                  className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[#FBF8F2] text-sm text-left"
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${allSelected ? 'bg-[#A62B1F] border-[#A62B1F]' : 'border-[#D3CCBE]'}`}>
                    {allSelected && <span className="text-white text-[10px] font-bold">✓</span>}
                  </span>
                  <span className="font-medium text-[#1E2A33]">All projects</span>
                </button>
                <div className="border-t border-[#F3EFE7] my-1" />
                {allProjects.map(p => {
                  const checked = visibleProjectIds === null || visibleProjectIds.includes(p._id);
                  return (
                    <button
                      key={p._id}
                      onClick={() => toggleProject(p._id)}
                      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[#FBF8F2] text-sm text-left"
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'border-transparent' : 'border-[#D3CCBE]'}`} style={checked ? { backgroundColor: p.color || '#A62B1F' } : {}}>
                        {checked && <span className="text-white text-[10px] font-bold">✓</span>}
                      </span>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#9AA3AB' }} />
                      <span className="text-[#1E2A33] truncate">{p.name}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="text-xs text-[#9AA3AB]">
          {totalAccounts} accounts · {visiblePipelines.length} of {projectPipelines.length} project{projectPipelines.length !== 1 ? 's' : ''}
        </div>

        <Button
          style={{ backgroundColor: '#A62B1F', color: 'white' }}
          onClick={() => handleAddAccount(allProjects[0] || null, 'new_lead')}
          className="text-sm ml-auto"
        >
          <Plus className="w-4 h-4 mr-1" /> New Account
        </Button>
      </div>

      {migrating && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-[#F7ECEA] border border-[#E7C3BC] text-sm text-[#4338CA] flex items-center gap-2">
          <Building2 className="w-4 h-4 flex-shrink-0 animate-pulse" />
          Linking contacts to accounts…
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-[#5C6670]" style={{ fontFamily: 'Manrope' }}>{t('pipeline.loading')}</p>
        </div>
      ) : visiblePipelines.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-[#9AA3AB]">
            {projectPipelines.length === 0 ? t('pipeline.no_projects') : 'No projects selected — use the Projects filter above.'}
          </p>
        </div>
      ) : (
        <div>
          {visiblePipelines.map(({ project, accounts, standalone_contacts }) => {
            const stages = project.pipeline_stages?.length ? project.pipeline_stages : DEFAULT_STAGES;
            return (
              <ProjectPipelineSection
                key={project._id}
                project={project}
                accounts={accounts || []}
                standaloneContacts={standalone_contacts || []}
                searchQuery={searchQuery}
                teamMap={teamMap}
                onAccountClick={(account) => {
                  const entry = (accounts || []).find(a => a.account._id === account._id);
                  const siblings = (accounts || []).map(a => a.account);
                  handleAccountClick(account, entry?.contacts || [], stages, entry?.branches || [], siblings);
                }}
                onContactClick={(contact) => handleContactClick(contact, stages)}
                onAddAccount={handleAddAccount}
                onUpdate={loadPipeline}
              />
            );
          })}
        </div>
      )}

      <AccountDetailSheet
        account={selectedAccount}
        contacts={selectedAccountContacts}
        branches={selectedAccountBranches}
        siblingAccounts={selectedAccountSiblings}
        stages={selectedAccountStages}
        isOpen={showAccountDetail}
        onClose={() => setShowAccountDetail(false)}
        onUpdate={loadPipeline}
        onOpenAccount={(account, contacts) => handleAccountClick(account, contacts, selectedAccountStages, [], selectedAccountSiblings)}
        teamMap={teamMap}
      />

      <ContactDetailSheet
        contact={selectedContact}
        stages={selectedContactStages}
        isOpen={showContactDetail}
        onClose={() => setShowContactDetail(false)}
        onUpdate={loadPipeline}
        teamMap={teamMap}
      />

      <NewAccountSheet
        isOpen={showNew}
        onClose={() => setShowNew(false)}
        onSuccess={loadPipeline}
        initialStage={newAccountStage}
        projects={allProjects}
        initialProject={newAccountProject}
      />
    </Layout>
  );
}
